import { inject } from '@angular/core';
import { ActivityEntityName, EntityViewDisplay, FeatureFlags, FinanceSecurityRight, FINANCIAL_DOCUMENT_PAYMENT_OPTION, Invoice, InvoiceType, SecurityModuleTag, SurfaceType } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import type { EntityManifestCard } from '@workbuddy/services/entities/entity.manifest';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { Command } from '@workbuddy/ui/model';
import { entityRecordUrl } from '@workbuddy/ui/v2/blocks/entity.record';
import { InvoiceAccountingCellRendererComponent } from './presentation/invoice.accounting.cell.renderer';
import { invoiceAccountingTag, invoiceStatusTag, invoiceTypeTag } from './presentation/invoice.status.colour';

// Registered when this manifest module is loaded at bootstrap. Consumers resolve it through
// getManifest/EntityManifestService; surface and component slots remain lazy.
// This is the Tenant manifest; portals get no Invoice UI until declared under `domains`.
const card: EntityManifestCard<Invoice> = {
  component: () => import('./presentation/card.invoice').then(m => m.CardInvoiceComponent),
  resolution: 'if-needed',
  isComplete: invoice => !!invoice.invoiceNumber,
  summarize: invoice => ({ title: invoice.invoiceNumber ?? (invoice as Invoice & { name?: string }).name ?? 'Not set' }),
};

export const invoiceManifest = registerManifest<Invoice>({
  entity: ActivityEntityName.Invoice,
  slug: 'invoices',
  featureFlag: FeatureFlags.FinanceDocsInvoiceV2,
  menu: {
    group: 'finance',
    label: 'Invoices',
    icon: 'fa-money-bills',
    order: 10,
    badge: SecurityModuleTag.Beta,
    authorize: () => inject(RightsService).hasRights(FinanceSecurityRight.CanAccessInvoices),
  },

  routes: {
    root: '/finance/invoices',
    // Credit notes and prepayments are invoices in the same collection and open here too.
    record: id => entityRecordUrl('invoices', 'preview', id),
  },

  board: {
    // Kanban is not meaningful for financial documents; locked manage preview is a per-view toggle.
    supportedDisplays: [EntityViewDisplay.Card, EntityViewDisplay.Table],
    // Document-shaped: the selection shows in the wide ~50/50 aside, not a narrow quickview.
    panel: { kind: 'preview' },
    defaults: {
      header: {
        title: 'Invoices',
        // Split +: the main click stays a plain invoice. The caret opens this same editor with a type.
        addCommands: [
          new Command({ label: 'New Credit Note', data: { type: InvoiceType.CreditNote } }),
          new Command({ label: 'New Prepayment', data: { type: InvoiceType.PrepaymentInvoice } }),
        ],
      },
      table: {
        overrideCellRenderer: (_context, _metadata, field, renderer) => field.name === 'integration'
          ? { ...renderer, cellRenderer: InvoiceAccountingCellRendererComponent }
          : renderer,
      },
    },
  },

  presentation: {
    card,
    tags: {
      status: invoiceStatusTag,
      type: invoiceTypeTag,
      integration: invoiceAccountingTag,
    },
  },

  surfaces: {
    [SurfaceType.FinancialDocument]: () => import('./surface/manage/invoice.manage.surface').then(m => m.invoiceSurface),
  },
  operations: {
    preview: {
      surface: SurfaceType.FinancialDocument,
      authorize: ({ injector }) => injector.get(RightsService).hasRights(FinanceSecurityRight.CanAccessInvoices),
      // The header's payment actions (card, allocate credit) are decided with the record, not after it.
      readOptions: [FINANCIAL_DOCUMENT_PAYMENT_OPTION],
    },
    create: { surface: SurfaceType.FinancialDocument, authorize: ({ injector }) => injector.get(RightsService).hasRights(FinanceSecurityRight.ManageInvoices) },
    // Read access mounts the document; write rights and lifecycle belong to surface `editableWhen`.
    edit: {
      surface: SurfaceType.FinancialDocument,
      authorize: ({ injector }) => injector.get(RightsService).hasRights(FinanceSecurityRight.CanAccessInvoices),
      // The header's payment actions (card, allocate credit) are decided with the record, not after it.
      readOptions: [FINANCIAL_DOCUMENT_PAYMENT_OPTION],
    },
  },

  relations: {
    [ActivityEntityName.Activity]: () => import('./relations/invoice.activities.relation').then(m => m.invoiceActivitiesBoardBuilder),
  },
});
